<template>
  <div class="w-full">
    <div
      v-show="isButtonFromSdk && !loading && !disabled"
      :id="buttonId"
      class="h-48 w-full"
    />
    <button
      v-show="!isButtonFromSdk || loading || disabled"
      :type="type"
      class="tw-provider__button"
      :class="{
        'tw-dark': darkMode,
        'tw-provider__button--disabled': disabled && !loading,
        'tw-provider__button--loading': loading,
        [provider]: true
      }"
      :disabled="disabled || loading"
    >
      <template v-if="loading">
        <WebSpinner
          :style="spinnerStyle"
          size="20"
          color="transparent"
          background="currentColor"
          class="mr-[10px]"
        />
      </template>
      <img
        :src="getAssetFromCdn(`editor/payment-providers/${provider}${disabled && !loading ? '_disabled' : darkMode ? '_black' : ''}.png`)"
        :alt="provider"
      />
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { PaymentProvider, type PaymentPageData } from '@zotlo-shared/elements/common/payment/types';
import { WebSpinner } from '@zotlo-shared/components';
import { getAssetFromCdn } from '@zotlo-shared/utils/helpers';

const { GOOGLE_PAY, APPLE_PAY, PIX } = PaymentProvider;

const props = withDefaults(defineProps<{
  type?: 'button' | 'submit';
  provider?: PaymentProvider;
  disabled?: boolean;
  loading?: boolean;
  darkMode?: boolean;
  isEditorMode?: boolean;
  isPreviewMode?: boolean;
  pageData: PaymentPageData;
}>(), {
  type: 'button',
  provider: PaymentProvider.PAYPAL,
  isEditorMode: true,
  pageData: () => ({} as PaymentPageData)
});

const buttonId = computed(() => `${props.provider}Button`);
const isGooglePayButtonFromSdk = computed(() => props.provider === GOOGLE_PAY && !props.isEditorMode && !props.isPreviewMode);
const isButtonFromSdk = computed(() => isGooglePayButtonFromSdk.value);
const spinnerStyle = computed(() => {
  if ([APPLE_PAY, GOOGLE_PAY, PIX].includes(props.provider)) {
    return {
      color: props.darkMode ? 'black' : 'white'
    }
  }
  return null;
})

function renderGooglePayButton() {
  const container = document?.getElementById(buttonId.value);
  const button = props.pageData?.getGooglePayButton({
    buttonColor: props.darkMode ? 'white' : 'black',
  });

  if (button && container) {
    // Show GPay button as decorator
    button.style.pointerEvents = 'none';
    button.setAttribute('aria-hidden', 'true');
    button.querySelector('button')?.setAttribute('tabindex', '-1');

    container?.appendChild(button);
  }
}

onMounted(() => {
  if (isGooglePayButtonFromSdk.value) renderGooglePayButton();
});
</script>

<style lang="postcss" scoped>
.tw-provider__button {
  @apply flex h-48 w-full items-center justify-center bg-[#000] rounded-[6px] p-8;
  img {
    @apply max-h-24;
  }

  &.paypal {
    @apply bg-[#FFC439];
    img {
      @apply max-h-20;
    }
  }

  &.tw-dark {
    @apply bg-white;
  }

  &.tw-provider__button {
    &--disabled {
      @apply !bg-neutral-200 !cursor-not-allowed;
    }
    &--loading {
      @apply !cursor-not-allowed;
    }
  }
}

:deep() {
  .gpay-card-info-container {
    @apply min-w-96;
    outline: none !important;
  }
  .gpay-card-info-animation-container {
    outline: none !important;
  }
}
</style>
